import { entity } from 'simpler-state';

/**
 * Dev-only tracker for the round-trip of a watchlist subscription batch: when the subscribe
 * request went out, and when the first chart and first price responses came back for those
 * symbols. Rendered by the `SubscriptionTimingDebug` panel under `?debug=performance`.
 *
 * Deliberately outside Redux/RxJS - the subscribe and response paths run through singleton
 * websocket services and module-level caches, so a module-level store is the natural fit and lets
 * the panel re-render on each milestone without extra wiring.
 */
export type SubscriptionTimingState = {
  label: string | null;
  sentAt: number | null;
  chartAt: number | null;
  priceAt: number | null;
};

const INITIAL_STATE: SubscriptionTimingState = {
  label: null,
  sentAt: null,
  chartAt: null,
  priceAt: null,
};

/**
 * One mount can fire more than one subscribe call. Calls sharing a label that land inside this
 * window join the same session rather than restarting the timings.
 */
const MERGE_WINDOW_MS = 800;

const store = entity(INITIAL_STATE);

// Session bookkeeping - deliberately non-reactive, nothing needs to re-render on these.
let sessionSymbols = new Set<string>();
let sessionStartedAt = 0;

/** A symbol can carry a share-class dot (BRK.B) on one path and a slash (BRK/B) on another. */
const normalize = (symbol: string): string => (symbol ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const belongsToSession = (symbols: string[]): boolean => symbols.some(item => sessionSymbols.has(normalize(item)));

const markSent = (label: string, symbols: string[]): void => {
  const now = Date.now();
  const current = store.get();
  const isSameSessionBurst = current.label === label
    && current.sentAt != null
    && now - sessionStartedAt < MERGE_WINDOW_MS;

  if (isSameSessionBurst) {
    symbols.forEach(item => sessionSymbols.add(normalize(item)));
    return;
  }

  sessionSymbols = new Set(symbols.map(normalize));
  sessionStartedAt = now;
  store.set({
    label, sentAt: now, chartAt: null, priceAt: null,
  });
};

const markChart = (symbols: string[]): void => {
  const current = store.get();
  if (current.sentAt == null || current.chartAt != null || !belongsToSession(symbols)) {
    return;
  }
  store.set({ ...current, chartAt: Date.now() });
};

const markPrice = (symbols: string[]): void => {
  const current = store.get();
  if (current.sentAt == null || current.priceAt != null || !belongsToSession(symbols)) {
    return;
  }
  store.set({ ...current, priceAt: Date.now() });
};

const reset = (): void => {
  sessionSymbols = new Set();
  sessionStartedAt = 0;
  store.set({ ...INITIAL_STATE });
};

export default {
  use: () => store.use(state => state),
  get: () => store.get(),
  markSent,
  markChart,
  markPrice,
  reset,
};
